// Ranked tier hierarchy for access checks.
export const CLINICAL_TIER_RANK = {
    free: 0,
    class_prep: 1, basic: 1, pro: 1,
    board_review: 2, elite: 2,
    specialty_boards: 3, clinicals: 3,
};

// Canonical training-profile → required entitlement tier for flashcard generation.
// Mirrors backend TIER_MAP (clinicals/examConfig.js) and userProfile.js's
// ROLE_TIER_MAP/SPECIALTY_BOARD_EXAM_TYPES. `null` = free, no gate.
// These are the ONLY values ever sent as clinicalSettings.examType — each is an
// exact key of backend EXAM_CONFIGS (clinicals/examConfig.js).
export const EXAM_TYPE_TIER = {
    // Class prep — med/PA/NP subjects
    anatomy: null, biochemistry: null, physiology: null, pharmacology: null,
    microbiology: null, pathology: null, immunology: null, neuroscience: null,
    // Class prep — nursing subjects
    nursing_fundamentals: null, nursing_medsurg: null, nursing_maternal: null,
    nursing_pediatric: null, nursing_psych: null, nursing_community: null,
    nursing_leadership: null, nursing_pharmacology: null, nursing_general: null,
    // Step 1 / Step 2 equivalent — free
    step1: null, comlex1: null, step2: null, comlex2: null, shelf: null,
    // Step 2 equivalent — advanced practice certification (paid)
    fnp: 'board_review', acnp: 'board_review', pance: 'board_review',
    crna: 'board_review', caa: 'board_review', npPaGeneral: 'board_review',
    // Nursing licensure (paid)
    nclexRn: 'board_review', nclexPn: 'board_review',
    // Step 3 & Specialty Boards
    step3: 'board_review', comlex3: 'board_review',
    specialtyBoard: 'specialty_boards',
};

// Display labels for the canonical examType keys above — used anywhere a
// human-readable "Prep Level" needs to be shown (Current Settings tile,
// upgrade banners) without needing the full onboarding role/option context.
export const EXAM_TYPE_LABELS = {
    anatomy: 'Anatomy', biochemistry: 'Biochemistry', physiology: 'Physiology',
    pharmacology: 'Pharmacology', microbiology: 'Microbiology', pathology: 'Pathology',
    immunology: 'Immunology', neuroscience: 'Neuroscience',
    nursing_fundamentals: 'Fundamentals of Nursing', nursing_medsurg: 'Medical-Surgical Nursing',
    nursing_maternal: 'Maternal-Newborn Nursing', nursing_pediatric: 'Pediatric Nursing',
    nursing_psych: 'Psychiatric-Mental Health', nursing_community: 'Community & Public Health',
    nursing_leadership: 'Leadership & Delegation', nursing_pharmacology: 'Pharmacology for Nursing',
    nursing_general: 'General Nursing (All Topics)',
    step1: 'USMLE Step 1', comlex1: 'COMLEX Level 1',
    step2: 'USMLE Step 2 CK', comlex2: 'COMLEX Level 2-CE', shelf: 'Shelf Exam',
    fnp: 'Family Nurse Practitioner (FNP)', acnp: 'Acute Care NP (ACNP)',
    pance: 'PA-C (PANCE/PANRE)', crna: 'CRNA (NBCRNA)', caa: 'CAA (ABA)',
    npPaGeneral: 'Other NP/PA/CRNA Program',
    nclexRn: 'NCLEX-RN', nclexPn: 'NCLEX-PN',
    step3: 'USMLE Step 3', comlex3: 'COMLEX Level 3',
    specialtyBoard: 'Specialty Board Certification',
};

// Required entitlement tier for a given canonical examType key. `null` = free.
export function getRequiredTierForExamType(examType) {
    return EXAM_TYPE_TIER[examType] ?? null;
}

// Discipline values that always collapse to the generic specialtyBoard config for
// generation — same prompting quality regardless of discipline (Step 3 & Specialty
// Boards tier). The discipline choice itself is still preserved in the onboarding
// profile's examTarget for analytics/marketing — this only affects what's actually
// sent to the generation engine.
const SPECIALTY_DISCIPLINE_VALUES = new Set([
    'internalMedicine', 'surgery', 'familyMedicine', 'pediatrics', 'obgyn',
    'emergencyMedicine', 'psychiatry', 'dermatology', 'ophthalmology', 'ent', 'specialtyBoard',
]);

// Resolves the canonical backend examType actually sent for generation, given a
// training-profile selection (role's subjects multi-select or examTarget single-select).
export function resolveGenerationExamType({ examTarget, subjects, isMultiSubject, activeSubject } = {}) {
    if (isMultiSubject) return activeSubject || subjects?.[0] || null;
    if (SPECIALTY_DISCIPLINE_VALUES.has(examTarget)) return 'specialtyBoard';
    return examTarget || null;
}

// Given a training-profile selection + the user's current entitlement tier, resolves
// the examType actually sent for generation — clamped to DEFAULT_EXAM_TYPE if the
// profile's true goal is above what the user has paid for. Never blocks the profile
// selection itself (that's tracked separately for analytics/upgrade-funnel purposes) —
// only clamps what's actually sent to the generation engine.
export function resolveClampedExamType({ examTarget, subjects, isMultiSubject, activeSubject, entitlementsTier } = {}) {
    const resolved = resolveGenerationExamType({ examTarget, subjects, isMultiSubject, activeSubject });
    const requiredTier = getRequiredTierForExamType(resolved);
    const currentTier = entitlementsTier || 'free';
    const isLocked = !!requiredTier && (CLINICAL_TIER_RANK[currentTier] || 0) < (CLINICAL_TIER_RANK[requiredTier] || 0);
    return { resolved, requiredTier, isLocked, examType: isLocked ? DEFAULT_EXAM_TYPE : resolved };
}

// Canonical exam-type options for backend exam GENERATION (POST /api/exams →
// clinicals/engine.js). `value` MUST be an exact key of EXAM_CONFIGS in the backend
// (clinicals/examConfig.js) — resolveExamConfig does an exact lookup with no
// normalization, so any unknown value silently falls back to `step2`. `label` is the
// user-facing string. Keep this list in sync with the backend EXAM_CONFIGS keys.
export const EXAM_TYPE_OPTIONS = [
  // USMLE / core
  { value: 'step1', label: 'USMLE Step 1' },
  { value: 'step2', label: 'USMLE Step 2 CK' },
  { value: 'step3', label: 'USMLE Step 3' },
  { value: 'shelf', label: 'Shelf Exam' },
  { value: 'specialtyBoard', label: 'Specialty Boards' },
  // COMLEX
  { value: 'comlex1', label: 'COMLEX Level 1' },
  { value: 'comlex2', label: 'COMLEX Level 2-CE' },
  { value: 'comlex3', label: 'COMLEX Level 3' },
  // Advanced practice / anesthesia
  { value: 'pance', label: 'PANCE / PANRE (Physician Assistant)' },
  { value: 'fnp', label: 'Family Nurse Practitioner (FNP)' },
  { value: 'acnp', label: 'Acute Care Nurse Practitioner (ACNP)' },
  { value: 'crna', label: 'Nurse Anesthetist (CRNA)' },
  { value: 'caa', label: 'Anesthesiologist Assistant (CAA)' },
  // Nursing licensure
  { value: 'nclexRn', label: 'NCLEX-RN (Registered Nurse)' },
  { value: 'nclexPn', label: 'NCLEX-PN (Practical/Vocational Nurse)' },
  // Specialty boards
  { value: 'internalMedicine', label: 'Internal Medicine Board (ABIM)' },
  { value: 'surgery', label: 'General Surgery Board (ABS)' },
  { value: 'familyMedicine', label: 'Family Medicine Board (ABFM)' },
  { value: 'pediatrics', label: 'Pediatrics Board (ABP)' },
  { value: 'obgyn', label: 'OB/GYN Board (ABOG)' },
  { value: 'emergencyMedicine', label: 'Emergency Medicine Board (ABEM)' },
  { value: 'psychiatry', label: 'Psychiatry Board (ABPN)' },
  { value: 'dermatology', label: 'Dermatology Board (ABD)' },
  { value: 'ophthalmology', label: 'Ophthalmology Board (ABO)' },
  { value: 'ent', label: 'Otolaryngology Board (ABOto)' },
];

// Default exam type for new users (matches backend DEFAULT_EXAM_CONFIG = step2).
export const DEFAULT_EXAM_TYPE = 'step2';

// Each style is a genuinely different flashcard format, not just a phrasing variant — see
// backend/clinicals/flashcardPromptBuilder.js. Rapid Recall and Clinical Vignette cover every
// fact domain (mechanism, management, criteria) within their own format; Differential Diagnosis
// is the one case where the answer is a ranked list rather than a single conclusion.
export const CLINICAL_CARD_STYLES = [
  'Rapid Recall',
  'Clinical Vignette',
  'Differential Diagnosis',
];

// "Differential Diagnosis" generates something genuinely different for nursing Prep Levels — a
// ranked ABC/Maslow priority list, never competing medical diagnoses (nurses don't diagnose).
// Show that in the displayed label so the dropdown/settings tile never silently means something
// other than what it says. The underlying stored value ("Differential Diagnosis") is unchanged —
// this only affects what's rendered.
export function cardStyleLabel(style, examType) {
  if (style === 'Differential Diagnosis' && String(examType || '').startsWith('nursing_')) {
    return 'Priority Concerns';
  }
  return style;
}

export const CLINICAL_INPUT_TYPES = [
  {
    id: 'notes',
    title: 'Notes / Text',
    description: 'Paste notes, clinical pearls, or lecture summaries',
    icon: 'notes',
  },
  {
    id: 'live',
    title: 'Live Audio',
    description: 'Capture live lectures or rounds in real time',
    icon: 'live',
  },
  {
    id: 'upload',
    title: 'Upload Audio/Video',
    description: 'Upload recordings from conferences or didactics',
    icon: 'upload',
  },
  {
    id: 'image',
    title: 'Image / Screenshot',
    description: 'Upload slides, textbook pages, or clinical images',
    icon: 'image',
  },
];

export const DEFAULT_CLINICAL_SETTINGS = {
  examType: DEFAULT_EXAM_TYPE,
  cardStyle: 'Rapid Recall',
  cardCount: 6,
};

// maxCards must not exceed MAX_COUNT in backend/src/clinical/integration/clinicalFlashcards.js
// (currently 15) — the backend silently clamps to its own cap regardless of what's offered
// here, so a higher number in this file would just mean the slider lies about what you get.
export const CLINICAL_TIER_LIMITS = {
    free:             { creditsPerMonth: 30,   maxCards: 5 },
    class_prep:       { creditsPerMonth: 1000, maxCards: 15 },
    board_review:     { creditsPerMonth: 2500, maxCards: 15 },
    specialty_boards: { creditsPerMonth: 4000, maxCards: 15 },
    // Legacy paid tiers that include Clinicals access
    pro:      { creditsPerMonth: 1200, maxCards: 15 },
    elite:    { creditsPerMonth: 2000, maxCards: 15 },
    clinicals:{ creditsPerMonth: 2500, maxCards: 15 },
};

export const GENERATOR_TIPS = [
  'Record, upload or snap a photo of your lecture study materials',
  'Set your Prep Level once in Settings — it\'s applied to every card you generate',
  'Switch to Clinical Vignette style for short case-based reasoning practice',
  'Use Differential Diagnosis style to drill ranked differentials from overlapping presentations',
  'Rapid Recall is the fastest way to drill straight facts between blocks',
];
